/**
 * Backlog API
 *
 * API functions for backlog reordering and bulk operations.
 *
 * Auto-prioritization (FLOW-886) is asynchronous: `POST
 * /projects/{id}/backlog/auto-prioritize` returns 202 immediately and the
 * model call runs in the background, so `autoPrioritize` no longer holds a
 * connection open for it — it posts, then polls `GET
 * .../backlog/auto-prioritize/status` via `waitForAutoPrioritize` until the
 * proposal is ready.
 */

import { api, ApiError, type AiServed } from "./client";
import type {
  ProjectId,
  WorkItem,
  AutoPrioritizeProposal,
  AutoPrioritizeRequest,
  AutoPrioritizeRunProgress,
  AutoPrioritizeStatus,
  BacklogReorderRequest,
  BulkReorderRequest,
  BulkReorderResponse,
  BulkStatusChangeRequest,
  BulkStatusChangeResponse,
  FocusDetection,
} from "./types";

export async function reorderBacklogItem(
  projectId: ProjectId,
  request: BacklogReorderRequest,
): Promise<WorkItem> {
  return api.post<WorkItem>(`/projects/${projectId}/backlog/reorder`, request);
}

export async function bulkChangeStatus(
  projectId: ProjectId,
  request: BulkStatusChangeRequest,
): Promise<BulkStatusChangeResponse> {
  return api.post<BulkStatusChangeResponse>(
    `/projects/${projectId}/work-items/bulk-status`,
    request,
  );
}

/** Poll delays: start at 1s, back off ×1.5, cap at 5s */
const POLL_INITIAL_MS = 1000;
const POLL_MAX_MS = 5000;
/** Give up waiting for the proposal after 180s */
const POLL_DEADLINE_MS = 180_000;

/** The proposal once auto-prioritization finishes, or a signal that it's still running */
export type AutoPrioritizeLookup =
  | { status: "processing"; progress: AutoPrioritizeRunProgress | null }
  | { status: "ready"; proposal: AiServed<AutoPrioritizeProposal> };

/**
 * Fetch the current auto-prioritize job status for a project. A 202 means
 * it's still running; a 404 ApiError means no job exists (thrown, not part
 * of the union).
 */
export async function getAutoPrioritizeStatus(
  projectId: ProjectId,
): Promise<AutoPrioritizeLookup> {
  // `getAiWithStatus`, not `getWithStatus`: the proposal's `degraded` is in the
  // body, but the ceiling that bound it only ever arrives on this response's
  // `x-ai-fallback` header — admission ran on the POST that answered 202
  // (FLOW-930).
  const { data, status } = await api.getAiWithStatus<
    AutoPrioritizeStatus | AutoPrioritizeProposal
  >(`/projects/${projectId}/backlog/auto-prioritize/status`);
  if (status !== 202) {
    return {
      status: "ready",
      proposal: data as AiServed<AutoPrioritizeProposal>,
    };
  }
  const body = data as AutoPrioritizeStatus;
  // `null` rather than a synthesised stage: a 202 with no stage is a backend
  // that predates FLOW-946, and inventing one here would misreport the run.
  // What the caller then shows is its own business — BacklogPage leaves its
  // bar on the "preparing" copy it set when the run started, so against such
  // a backend the bar reads "Reading the backlog…" for the whole run.
  return {
    status: "processing",
    progress: body.stage
      ? { stage: body.stage, progress: body.progress }
      : null,
  };
}

/**
 * Poll until a running auto-prioritization finishes. A 404 mid-poll means
 * the job vanished (backend restart, or it was already read once) —
 * surfaced as a 502 rather than the raw 404 so it doesn't read as "no job
 * was ever requested".
 *
 * `shouldStop`, when given, is checked right after each sleep and BEFORE
 * issuing the next GET — so a caller that has gone away (component
 * disposed, or the user switched projects) stops polling instead of
 * racing a fresh poller for the same project's read-once result. Callers
 * that don't need this (tests, non-UI call sites) can omit it; the loop
 * behaves exactly as before.
 *
 * `onProgress` fires once per in-flight poll that names a stage, never after
 * the proposal lands.
 */
export async function waitForAutoPrioritize(
  projectId: ProjectId,
  shouldStop?: () => boolean,
  onProgress?: (p: AutoPrioritizeRunProgress) => void,
): Promise<AiServed<AutoPrioritizeProposal>> {
  const started = Date.now();
  let delay = POLL_INITIAL_MS;

  for (;;) {
    await new Promise((resolve) => setTimeout(resolve, delay));

    if (shouldStop?.()) {
      // Not a server failure — the caller stopped caring. 499 (nginx's
      // "Client Closed Request") signals "cancelled", not "broken"; the
      // only consumer is `handleAutoPrioritize`'s disposed/project-switch
      // guard, which swallows this before it ever reaches a toast.
      throw new ApiError(499, "Prioritization poll cancelled.");
    }

    let lookup: AutoPrioritizeLookup;
    try {
      lookup = await getAutoPrioritizeStatus(projectId);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        throw new ApiError(
          502,
          "Prioritization was interrupted. Please try again.",
        );
      }
      throw err;
    }

    if (lookup.status === "ready") return lookup.proposal;

    if (lookup.progress) onProgress?.(lookup.progress);

    if (Date.now() - started > POLL_DEADLINE_MS) {
      throw new ApiError(408, "Prioritization timed out. Please retry.");
    }

    delay = Math.min(delay * 1.5, POLL_MAX_MS);
  }
}

/**
 * Start (or join) a run and wait for its proposal. `joined` is true when the
 * POST attached to a job that was already in flight for the project: the
 * proposal then belongs to that job's focus, not to `focus`, so callers must
 * not attribute it to the focus they passed.
 *
 * `onProgress` fires once per in-flight poll that names a stage, never after
 * the proposal lands.
 */
export async function autoPrioritize(
  projectId: ProjectId,
  focus: string | null,
  shouldStop?: () => boolean,
  onProgress?: (p: AutoPrioritizeRunProgress) => void,
): Promise<AiServed<AutoPrioritizeProposal> & { joined: boolean }> {
  const body: AutoPrioritizeRequest = { focus };
  const started = await api.post<AutoPrioritizeStatus>(
    `/projects/${projectId}/backlog/auto-prioritize`,
    body,
  );
  return {
    ...(await waitForAutoPrioritize(projectId, shouldStop, onProgress)),
    joined: started.joined === true,
  };
}

/**
 * Detect the project's current focus from its Business Priority, published
 * roadmap goals and recent activity (FLOW-920). Synchronous — unlike
 * `autoPrioritize`, there's no job to poll for.
 */
export async function detectAutoPrioritizeFocus(
  projectId: ProjectId,
): Promise<AiServed<FocusDetection>> {
  return api.postAi<FocusDetection>(
    `/projects/${projectId}/backlog/auto-prioritize/focus`,
    {},
  );
}

export async function bulkReorder(
  projectId: ProjectId,
  request: BulkReorderRequest,
): Promise<BulkReorderResponse> {
  return api.post<BulkReorderResponse>(
    `/projects/${projectId}/backlog/bulk-reorder`,
    request,
  );
}
